class LookbookSlider extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.realSlides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));
    this.loop = this.dataset.loop !== 'false';
    this.isSliderStrip = this.slider.classList.contains('lookbook__strip--slider');
    this.prevBtn = this.querySelector('button[name="previous"]');
    this.nextBtn = this.querySelector('button[name="next"]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.speed = (Number(this.dataset.speed) || 5) * 1000;
    this.autoplaySuspended = false;
    this.isNormalizing = false;
    this.realCount = this.realSlides.length;

    this.initVideoSlides();

    if (this.loop && this.isSliderStrip && this.realCount > 1) {
      this.setupInfiniteLoop();
    }

    this.slides = Array.from(this.slider.querySelectorAll('.lookbook__item'));
    this.initSlideVisibility();

    if (this.realCount < 2) return;

    this.measure();
    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      const realIndex = this.getNearestRealIndex();
      this.scrollToRealIndex(realIndex, false);
    });
    this.resizeObserver.observe(this.slider);

    this.onScroll = () => {
      if (this.isNormalizing) return;
      this.updateButtons();
      this.scheduleNormalize();
    };
    this.onScrollEndHandler = () => {
      if (this.loop && this.isSliderStrip) this.normalizeInfiniteScroll();
    };
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    this.slider.addEventListener('scrollend', this.onScrollEndHandler, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    if (this.autoplayEnabled) {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      });
      this.play();
    }

    this.updateButtons();
  }

  disconnectedCallback() {
    this.pause();
    clearTimeout(this.normalizeTimer);
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this._mobileQuery?.removeEventListener('change', this._onMobileQueryChange);
    this.slider?.removeEventListener('scroll', this.onScroll);
    this.slider?.removeEventListener('scrollend', this.onScrollEndHandler);
    this.videoControllers?.forEach((controller) => controller.destroy());
  }

  scheduleNormalize() {
    if (!this.loop || !this.isSliderStrip) return;
    clearTimeout(this.normalizeTimer);
    this.normalizeTimer = setTimeout(() => this.normalizeInfiniteScroll(), 120);
  }

  setupInfiniteLoop() {
    if (this.slider.dataset.lookbookInfiniteReady === 'true') return;
    this.slider.dataset.lookbookInfiniteReady = 'true';
    this.slider.classList.add('lookbook__strip--loop');

    const fragmentBefore = document.createDocumentFragment();
    [...this.realSlides].reverse().forEach((slide) => {
      fragmentBefore.prepend(this.createClone(slide));
    });
    this.slider.insertBefore(fragmentBefore, this.realSlides[0]);

    const fragmentAfter = document.createDocumentFragment();
    this.realSlides.forEach((slide) => {
      fragmentAfter.appendChild(this.createClone(slide));
    });
    this.slider.appendChild(fragmentAfter);

    requestAnimationFrame(() => {
      this.measure();
      this.scrollToRealIndex(0, false);
    });
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);
    clone.classList.add('lookbook__item--clone');
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[data-lookbook-video]').forEach((node) => {
      node.removeAttribute('data-lookbook-video');
      node.querySelector('[data-lookbook-play]')?.remove();
    });
    return clone;
  }

  getMiddleSetScrollBounds() {
    const first = this.realSlides[0];
    const last = this.realSlides[this.realCount - 1];
    if (!first || !last) return null;

    const min = this.getScrollLeftForCenteredSlide(first);
    const max = this.getScrollLeftForCenteredSlide(last);
    const setWidth = this.slideOffset * this.realCount;

    return { min, max, setWidth };
  }

  normalizeInfiniteScroll() {
    if (!this.loop || !this.isSliderStrip || this.realCount < 2 || this.isNormalizing) return;

    const bounds = this.getMiddleSetScrollBounds();
    if (!bounds) return;

    const { min, max, setWidth } = bounds;
    const x = this.slider.scrollLeft;
    const edge = Math.max(4, this.slideOffset * 0.05);

    if (x < min - edge) {
      this.jumpScroll(x + setWidth);
      return;
    }

    if (x > max + edge) {
      this.jumpScroll(x - setWidth);
    }
  }

  jumpScroll(left) {
    this.isNormalizing = true;
    this.slider.classList.add('lookbook__strip--normalizing');
    const previousBehavior = this.slider.style.scrollBehavior;
    this.slider.style.scrollBehavior = 'auto';
    this.slider.scrollLeft = left;
    this.slider.style.scrollBehavior = previousBehavior;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isNormalizing = false;
        this.slider.classList.remove('lookbook__strip--normalizing');
      });
    });
  }

  scrollToRealIndex(index, smooth = true) {
    this.measure();
    const slide = this.realSlides[index];
    if (!slide) return;
    const target = this.getScrollLeftForCenteredSlide(slide);
    this.slider.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'instant' });
  }

  getScrollLeftForCenteredSlide(slide) {
    const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
    return slideCenter - this.slider.clientWidth / 2;
  }

  getNearestRealIndex() {
    if (!this.realSlides.length) return 0;

    const viewportCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;

    this.realSlides.forEach((slide, index) => {
      const itemCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(itemCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  getNearestSlideIndex() {
    const items = Array.from(this.slider.querySelectorAll('.lookbook__item'));
    if (!items.length) return 0;

    const viewportCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;

    items.forEach((item, index) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  initSlideVisibility() {
    if (!this.slider || !this.slides?.length) return;

    this._mobileQuery = window.matchMedia('(max-width: 749px)');

    const bindObserver = () => {
      this.visibilityObserver?.disconnect();
      const items = Array.from(this.slider.querySelectorAll('.lookbook__item:not(.lookbook__item--clone)'));

      if (!this._mobileQuery.matches) {
        Array.from(this.slider.querySelectorAll('.lookbook__item')).forEach((slide) => {
          slide.classList.remove('is-in-view');
        });
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (this.isNormalizing) return;
          entries.forEach((entry) => {
            entry.target.classList.toggle(
              'is-in-view',
              entry.isIntersecting && entry.intersectionRatio >= 0.55
            );
          });
        },
        { root: this.slider, threshold: [0, 0.35, 0.55, 0.75, 1] }
      );

      items.forEach((slide) => this.visibilityObserver.observe(slide));
    };

    this._onMobileQueryChange = bindObserver;
    bindObserver();
    this._mobileQuery.addEventListener('change', this._onMobileQueryChange);
  }

  initVideoSlides() {
    this.videoControllers = [];
    this.querySelectorAll('[data-lookbook-video]').forEach((root) => {
      this.videoControllers.push(new LookbookVideoSlide(root, this));
    });
  }

  pauseForVideo() {
    this.autoplaySuspended = true;
    this.pause();
  }

  resumeAfterVideo() {
    this.autoplaySuspended = false;
    if (this.autoplayEnabled) this.play();
  }

  measure() {
    const sample = this.realSlides?.[0] || this.slides?.[0];
    if (!sample) return;

    const second = this.realSlides?.[1] || this.slides?.[1];
    if (second) {
      this.slideOffset = second.offsetLeft - sample.offsetLeft;
    }
    if (!this.slideOffset) this.slideOffset = sample.offsetWidth + this.getGap();

    const pad = Math.max(0, (this.slider.clientWidth - sample.offsetWidth) / 2);
    this.slider.style.setProperty('--lookbook-center-pad', `${pad}px`);
  }

  getGap() {
    const styles = window.getComputedStyle(this.slider);
    return parseFloat(styles.columnGap || styles.gap) || 4;
  }

  maxScroll() {
    return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
  }

  step(direction) {
    this.measure();

    if (this.loop && this.isSliderStrip) {
      const nextReal =
        (this.getNearestRealIndex() + direction + this.realCount) % this.realCount;
      this.scrollToRealIndex(nextReal, true);
      this.restartAutoplay();
      return;
    }

    const items = Array.from(this.slider.querySelectorAll('.lookbook__item'));
    if (!items.length) return;

    const nextIndex = Math.max(
      0,
      Math.min(items.length - 1, this.getNearestSlideIndex() + direction)
    );
    const target = this.getScrollLeftForCenteredSlide(items[nextIndex]);
    this.slider.scrollTo({ left: target, behavior: 'smooth' });
    this.restartAutoplay();
  }

  play() {
    if (this.autoplaySuspended) return;
    clearInterval(this.timer);
    this.timer = setInterval(() => this.step(1), this.speed);
  }

  pause() {
    clearInterval(this.timer);
  }

  resume() {
    if (this.autoplayEnabled && !this.autoplaySuspended) this.play();
  }

  restartAutoplay() {
    if (!this.autoplayEnabled || this.autoplaySuspended) return;
    this.pause();
    this.play();
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn || this.loop) return;
    const max = this.maxScroll();
    this.prevBtn.toggleAttribute('disabled', this.slider.scrollLeft <= 1);
    this.nextBtn.toggleAttribute('disabled', this.slider.scrollLeft >= max - 1);
  }
}

class LookbookVideoSlide {
  constructor(root, slider) {
    this.root = root;
    this.slider = slider;
    this.video = root.querySelector('video');
    this.playBtn = root.querySelector('[data-lookbook-play]');
    this.poster = root.querySelector('[data-lookbook-poster]');
    this.isPlaying = false;
    this.isPaused = false;
    this.isStarting = false;
    this.warmed = false;

    if (!this.video) return;

    this.video.muted = true;
    this.video.loop = false;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    this.video.removeAttribute('controls');
    this.video.pause();

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.onWarmUp = this.onWarmUp.bind(this);

    this.item = root.closest('.lookbook__item--video') || root;
    this.item.addEventListener('click', this.onToggleClick);
    this.playBtn?.addEventListener('pointerenter', this.onWarmUp);
    this.playBtn?.addEventListener('focus', this.onWarmUp);
    this.video.addEventListener('ended', this.onEnded);
    this.video.addEventListener('playing', this.onPlaying);

    this.observer = new IntersectionObserver(this.onIntersect, {
      root: slider.querySelector('[id^="Slider-"]') || null,
      rootMargin: '0px 80px',
      threshold: [0, 0.2, 0.55],
    });
    this.observer.observe(this.root);
  }

  destroy() {
    this.observer?.disconnect();
    this.item?.removeEventListener('click', this.onToggleClick);
    this.playBtn?.removeEventListener('pointerenter', this.onWarmUp);
    this.playBtn?.removeEventListener('focus', this.onWarmUp);
    this.video?.removeEventListener('ended', this.onEnded);
    this.video?.removeEventListener('playing', this.onPlaying);
  }

  onWarmUp() {
    this.warmUp();
  }

  warmUp() {
    if (!this.video || this.warmed) return;
    this.warmed = true;
    this.video.preload = 'auto';
    if (this.video.readyState === HTMLMediaElement.HAVE_NOTHING) {
      this.video.load();
    }
  }

  whenCanPlay() {
    if (!this.video) return Promise.resolve();
    if (this.video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const done = () => {
        this.video.removeEventListener('canplay', done);
        this.video.removeEventListener('error', done);
        resolve();
      };
      this.video.addEventListener('canplay', done, { once: true });
      this.video.addEventListener('error', done, { once: true });
      this.warmUp();
    });
  }

  onPlaying() {
    this.isStarting = false;
    this.isPlaying = true;
    if (this.poster) this.poster.hidden = true;
    this.root.classList.remove('is-buffering');
  }

  onIntersect(entries) {
    const entry = entries[0];
    if (!entry) return;

    if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
      this.warmUp();
      return;
    }

    if (!entry.isIntersecting && (this.isPlaying || this.isStarting)) {
      this.stopPlayback({ resumeSlider: true });
    }
  }

  onToggleClick(event) {
    if (!this.root.contains(event.target)) return;
    event.preventDefault();
    this.togglePlayback();
  }

  togglePlayback() {
    if (this.isPlaying || this.isStarting) {
      this.pausePlayback();
      return;
    }

    this.slider?.videoControllers?.forEach((controller) => {
      if (controller !== this) controller.stopPlayback({ resumeSlider: false });
    });
    this.startPlayback();
  }

  async startPlayback() {
    if (!this.video || this.isPlaying || this.isStarting) return;

    this.isStarting = true;
    this.video.loop = false;
    this.root.classList.add('is-user-playing', 'is-buffering');
    this.slider?.pauseForVideo();

    this.warmUp();
    await this.whenCanPlay();

    if (!this.isStarting) return;

    const resumeFromPause =
      this.isPaused && this.video.currentTime > 0 && !this.video.ended;

    this.isPaused = false;
    this.video.muted = false;

    if (!resumeFromPause) {
      this.video.currentTime = 0;
    }

    try {
      await this.video.play();
    } catch (error) {
      this.stopPlayback({ resumeSlider: true });
    }
  }

  pausePlayback({ resumeSlider = true } = {}) {
    if (!this.video) return;
    this.isStarting = false;
    this.isPlaying = false;
    this.isPaused = true;
    this.video.pause();
    this.root.classList.remove('is-user-playing', 'is-buffering');
    if (this.poster) this.poster.hidden = true;
    if (resumeSlider) this.slider?.resumeAfterVideo();
  }

  stopPlayback({ resumeSlider = false } = {}) {
    if (!this.video) return;
    this.isStarting = false;
    this.isPlaying = false;
    this.isPaused = false;
    this.video.pause();
    this.video.muted = true;
    this.video.currentTime = 0;
    this.root.classList.remove('is-user-playing', 'is-buffering');
    if (this.poster) this.poster.hidden = false;
    if (resumeSlider) this.slider?.resumeAfterVideo();
  }

  onEnded() {
    this.stopPlayback({ resumeSlider: true });
  }
}

customElements.define('lookbook-slider', LookbookSlider);
